import React, { useState, useEffect, useRef } from 'react';
import { useConfig } from '../config/ConfigContext';
import communityService from '../services/communityService';
import * as donationMessagingService from '../services/donationMessagingService';
import FloatingShareButton from '../components/FloatingShareButton';
import SponsorWidget from '../components/announcements/SponsorWidget';
import SponsorshipFlow from '../components/sponsorship/SponsorshipFlow';
import PDFViewer from '../components/PDFViewer';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import useMediaQuery from '../hooks/useMediaQuery';
import './AnnouncementsPage.css';

/**
 * HtmlContent Component
 * Renders HTML content with proper formatting using DOM manipulation
 * Replicates Material-UI's Box component approach
 */
const HtmlContent = ({ content, className }) => {
  const contentRef = useRef(null);
  
  useEffect(() => {
    if (contentRef.current) {
      // Apply styles directly to all elements after render
      const applyStyles = () => {
        // Paragraphs
        const paragraphs = contentRef.current.querySelectorAll('p');
        paragraphs.forEach(p => {
          p.style.display = 'block';
          p.style.margin = '0 0 1em 0';
          p.style.lineHeight = '1.6';
          p.style.fontSize = '15px';
          p.style.color = '#666';
        });
        
        // Remove margin from last paragraph
        if (paragraphs.length > 0) {
          paragraphs[paragraphs.length - 1].style.marginBottom = '0';
        }
        
        // Strong/Bold
        const strongs = contentRef.current.querySelectorAll('strong, b');
        strongs.forEach(s => {
          s.style.fontWeight = '700';
          s.style.color = '#333';
        });
        
        // Emphasis/Italic
        const ems = contentRef.current.querySelectorAll('em, i');
        ems.forEach(em => {
          em.style.fontStyle = 'italic';
        });
        
        // Lists
        const lists = contentRef.current.querySelectorAll('ul, ol');
        lists.forEach(list => {
          list.style.margin = '0.5em 0 1em 0';
          list.style.paddingLeft = '1.5em';
          list.style.display = 'block';
          if (list.tagName === 'UL') {
            list.style.listStyle = 'disc outside';
          } else {
            list.style.listStyle = 'decimal outside';
          }
        });
        
        // List items
        const listItems = contentRef.current.querySelectorAll('li');
        listItems.forEach(li => {
          li.style.margin = '0.25em 0';
          li.style.display = 'list-item';
          li.style.fontSize = '15px';
          li.style.lineHeight = '1.6';
        });
        
        // Line breaks
        const breaks = contentRef.current.querySelectorAll('br');
        breaks.forEach(br => {
          br.style.display = 'block';
          br.style.marginBottom = '0.5em';
        });
        
        // Headings
        const headings = contentRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headings.forEach(h => {
          h.style.fontWeight = '600';
          h.style.margin = '1em 0 0.5em 0';
          h.style.lineHeight = '1.3';
          h.style.color = '#333';
        });
        
        // Blockquotes
        const quotes = contentRef.current.querySelectorAll('blockquote');
        quotes.forEach(q => {
          q.style.borderLeft = '3px solid #ccc';
          q.style.paddingLeft = '1em';
          q.style.margin = '1em 0';
          q.style.fontStyle = 'italic';
          q.style.color = '#999';
        });
      };
      
      // Apply styles immediately
      applyStyles();
      
      // Also apply after a small delay to ensure DOM is fully ready
      setTimeout(applyStyles, 100);
    }
  }, [content]);
  
  // Handle plain text content
  const processContent = (rawContent) => {
    if (!rawContent) return '';
    
    // If no HTML tags, convert line breaks to paragraphs
    if (!rawContent.includes('<p>') && !rawContent.includes('<div>') && !rawContent.includes('<br')) {
      return rawContent
        .split('\n')
        .filter(line => line.trim())
        .map(line => `<p>${line}</p>`)
        .join('');
    }
    return rawContent;
  };
  
  return (
    <div 
      ref={contentRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: processContent(content) }}
    />
  );
};

/**
 * AnnouncementsPage Component
 * Displays community announcements fetched from Supabase
 * Beautiful, modern design matching Penta_Uni_Platform aesthetics
 */
const AnnouncementsPage = () => {
  const { config, theme } = useConfig();
  const [announcements, setAnnouncements] = useState([]);
  const [expiredAnnouncements, setExpiredAnnouncements] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [selectedImage, setSelectedImage] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfAnnouncement, setPdfAnnouncement] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReactions, setUserReactions] = useState({});
  const [processingReaction, setProcessingReaction] = useState(false);
  const [targetAnnouncementId, setTargetAnnouncementId] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const deepLinkNotFoundShownRef = useRef(false);
  
  // Helper function to detect if URL is a PDF
  const isPdfUrl = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    
    // Check for common PDF indicators
    return urlLower.endsWith('.pdf') || 
           urlLower.includes('.pdf?') || 
           urlLower.includes('/pdf/') ||
           urlLower.includes('type=application/pdf') ||
           urlLower.includes('export?format=pdf') ||
           // Google Drive document export as PDF
           (urlLower.includes('docs.google.com') && urlLower.includes('/export?format=pdf')) ||
           // Dropbox PDF files
           (urlLower.includes('dropbox.com') && urlLower.includes('.pdf')) ||
           // Supabase storage with PDF
           (urlLower.includes('supabase') && urlLower.includes('.pdf'));
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [config]);

  // Check for payment success parameters in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const amount = urlParams.get('amount');
    const announcementId = urlParams.get('announcement_id');
    
    if (paymentSuccess === 'true') {
      // Show success message
      setShowPaymentSuccess(true);
      
      // Clear the URL parameters
      const newUrl = announcementId 
        ? `${window.location.pathname}#announcement-${announcementId}`
        : window.location.pathname;
      window.history.replaceState(null, '', newUrl);
      
      // Auto-hide the success message after 5 seconds
      setTimeout(() => {
        setShowPaymentSuccess(false);
      }, 5000);
    }
  }, []);

  // Parse deep link target from URL on mount and when hash changes
  useEffect(() => {
    const parseFromUrl = () => {
      const hash = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);
      const shareId = urlParams.get('id') || urlParams.get('share');

      let id = null;
      if (shareId) {
        // Coming from share URL: normalize to hash for better UX
        id = String(shareId);
        const newUrl = `${window.location.pathname}#announcement-${id}`;
        window.history.replaceState(null, '', newUrl);
      } else if (hash && hash.startsWith('#announcement-')) {
        id = hash.replace('#announcement-', '');
      }

      setTargetAnnouncementId(id || null);
    };

    parseFromUrl();

    const onHashChange = () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith('#announcement-')) {
        setTargetAnnouncementId(hash.replace('#announcement-', '') || null);
      } else {
        setTargetAnnouncementId(null);
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Handle deep linking once announcements are loaded and target is known
  useEffect(() => {
    if (loading) return;

    if (!targetAnnouncementId) {
      // Reset to default meta tags for the announcements page
      updateMetaTags(null);
      return;
    }

    const idStr = String(targetAnnouncementId);
    const announcement = announcements.find(a => String(a.id) === idStr);

    if (announcement) {
      updateMetaTags(announcement);

      // Scroll to the announcement after a brief delay
      setTimeout(() => {
        const element = document.getElementById(`announcement-${idStr}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          element.style.boxShadow = '0 0 20px rgba(26, 65, 115, 0.3)';
          setTimeout(() => {
            element.style.boxShadow = '';
          }, 2000);
        }
      }, 500);
    } else {
      if (!deepLinkNotFoundShownRef.current) {
        showToast('Announcement not found or may have been removed.', true);
        deepLinkNotFoundShownRef.current = true;
      }
      updateMetaTags(null);
    }
  }, [announcements, targetAnnouncementId, loading]);

  // Function to update Open Graph meta tags
  const updateMetaTags = (announcement) => {
    const metaTags = {
      'og:title': announcement ? announcement.title : 'Announcements',
      'og:description': announcement ? truncateContent(announcement.content) : `Latest announcements and events from ${config?.name || 'our community'}`,
      'og:image': announcement?.image_url || config?.logo || '/images/og-default.jpg',
      'og:url': window.location.href,
      'og:type': 'article',
      'twitter:card': announcement?.image_url ? 'summary_large_image' : 'summary',
      'twitter:title': announcement ? announcement.title : 'Announcements',
      'twitter:description': announcement ? truncateContent(announcement.content) : `Latest announcements and events`,
      'twitter:image': announcement?.image_url || config?.logo || '/images/og-default.jpg'
    };

    // Update or create meta tags
    Object.entries(metaTags).forEach(([property, content]) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.querySelector(`meta[name="${property}"]`);
      }
      
      if (metaTag) {
        metaTag.setAttribute('content', content);
      } else {
        metaTag = document.createElement('meta');
        if (property.startsWith('twitter:')) {
          metaTag.setAttribute('name', property);
        } else {
          metaTag.setAttribute('property', property);
        }
        metaTag.setAttribute('content', content);
        document.head.appendChild(metaTag);
      }
    });

    // Update the page title
    document.title = announcement ? 
      `${announcement.title} | ${config?.name || 'Announcements'}` : 
      `Announcements | ${config?.name || 'Penta University'}`;
      
    // Add structured data for rich snippets
    if (announcement) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        'name': announcement.title,
        'description': truncateContent(announcement.content),
        'image': announcement.image_url || config?.logo,
        'startDate': announcement.created_at,
        'eventStatus': 'https://schema.org/EventScheduled',
        'eventAttendanceMode': 'https://schema.org/MixedEventAttendanceMode',
        'organizer': {
          '@type': 'Organization',
          'name': config?.name || 'Community Center',
          'url': window.location.origin
        },
        'location': {
          '@type': 'Place',
          'name': config?.name || 'Community Center',
          'address': config?.address || ''
        }
      };
      
      // Extract event date if found in content
      const eventDates = extractDatesFromAnnouncement(announcement);
      if (eventDates.length > 0) {
        structuredData.startDate = eventDates[0].toISOString();
      }
      
      scriptTag.textContent = JSON.stringify(structuredData);
    }
  };

  const fetchAnnouncements = async () => {
    // Use center_id (UUID) if available, otherwise fall back to id
    const centerId = config?.center_id || config?.id;
    
    // Debug: Log the full config to see if payment settings are loaded
    console.log('[DEBUG] Full config object:', config);
    console.log('[DEBUG] Payment config:', config?.community_features?.payment);
    
    if (!centerId) {
      setError('No center configuration found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching announcements for center:', centerId, '(center_id:', config.center_id, ', id:', config.id, ')');
      const [data, expired] = await Promise.all([
        communityService.getAnnouncements(centerId),
        communityService.getExpiredAnnouncements(centerId),
      ]);
      console.log('Fetched announcements:', data?.length, 'active,', expired?.length, 'expired');
      setAnnouncements(data || []);
      setExpiredAnnouncements(expired || []);

      // Fetch reaction counts and user reactions
      if (data && data.length > 0) {
        await fetchReactions(data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const fetchReactions = async (announcementsData) => {
    try {
      const announcementIds = announcementsData.map(a => a.id);
      
      // Get reaction counts for all announcements
      const counts = await communityService.getReactionCounts(announcementIds);
      setReactionCounts(counts || {});
      
      // Get or create a session ID for anonymous reactions
      const sessionId = getOrCreateSessionId();
      const userReactionData = await communityService.getUserReactions(sessionId, announcementIds);
      
      // Convert to a more convenient format
      const userReactionsMap = {};
      userReactionData.forEach(item => {
        userReactionsMap[item.announcement_id] = item.reaction_types;
      });
      setUserReactions(userReactionsMap);
    } catch (err) {
      console.error('Error fetching reactions:', err);
      // Don't show error to user, reactions are non-critical
    }
  };

  // Get or create a unique session ID for anonymous reactions
  const getOrCreateSessionId = () => {
    let sessionId = sessionStorage.getItem('penta_session_id');
    
    // UUID v4 validation regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    
    // Check if session ID exists and is a valid UUID v4
    if (!sessionId || !uuidRegex.test(sessionId)) {
      // Log if we're replacing an invalid session ID
      if (sessionId && !uuidRegex.test(sessionId)) {
        console.log('Replacing invalid session ID:', sessionId);
      }
      
      // Generate a proper UUID v4 for session ID
      sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
      sessionStorage.setItem('penta_session_id', sessionId);
      console.log('Generated new UUID v4 session ID:', sessionId);
    }
    return sessionId;
  };

  const handleReaction = async (event, announcementId) => {
    event.stopPropagation(); // Prevent card expansion
    
    if (processingReaction) return;
    
    try {
      setProcessingReaction(true);
      
      // Get or create session ID for anonymous reactions
      const sessionId = getOrCreateSessionId();
      
      // Toggle reaction
      const result = await communityService.toggleReaction(announcementId, sessionId, 'like');
      
      // Update local state optimistically
      const currentUserReactions = userReactions[announcementId] || [];
      const currentCount = reactionCounts[announcementId]?.like || 0;
      
      if (result.action === 'removed') {
        // Remove reaction
        setUserReactions(prev => ({
          ...prev,
          [announcementId]: currentUserReactions.filter(r => r !== 'like')
        }));
        setReactionCounts(prev => ({
          ...prev,
          [announcementId]: {
            ...prev[announcementId],
            like: Math.max(0, currentCount - 1)
          }
        }));
      } else {
        // Add reaction
        setUserReactions(prev => ({
          ...prev,
          [announcementId]: [...currentUserReactions, 'like']
        }));
        setReactionCounts(prev => ({
          ...prev,
          [announcementId]: {
            ...prev[announcementId],
            like: currentCount + 1
          }
        }));
      }
    } catch (err) {
      console.error('Error toggling reaction:', err);
      // Silently fail for better UX
    } finally {
      setProcessingReaction(false);
    }
  };

  const toggleExpanded = (announcementId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(announcementId)) {
        newSet.delete(announcementId);
      } else {
        newSet.add(announcementId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
      }
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 168) { // Less than a week
      const days = Math.floor(diffInHours / 24);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const truncateContent = (content, maxLength = 150) => {
    if (!content) return '';
    // Remove HTML tags for preview
    const plainText = content.replace(/<[^>]*>/g, '');
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  // Decode HTML entities to clean text
  const decodeHtmlEntities = (text) => {
    const entities = {
      '&nbsp;': ' ',
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
      '&ndash;': '–',
      '&mdash;': '—',
      '&hellip;': '…',
      '&copy;': '©',
      '&reg;': '®',
      '&trade;': '™',
      '&ldquo;': '"',
      '&rdquo;': '"',
      '&lsquo;': "'",
      '&rsquo;': "'",
      '&bull;': '•',
      '&middot;': '·'
    };
    
    // Decode named entities
    let decodedText = text;
    for (const [entity, char] of Object.entries(entities)) {
      decodedText = decodedText.replace(new RegExp(entity, 'gi'), char);
    }
    
    // Decode numeric entities (e.g., &#160; for nbsp)
    decodedText = decodedText.replace(/&#(\d+);/g, (match, code) => {
      return String.fromCharCode(parseInt(code, 10));
    });
    
    // Decode hex entities (e.g., &#xA0; for nbsp)
    decodedText = decodedText.replace(/&#x([0-9A-F]+);/gi, (match, code) => {
      return String.fromCharCode(parseInt(code, 16));
    });
    
    return decodedText;
  };

  // Format content for sharing with proper spacing and line breaks
  const formatShareText = (content, maxLength = 300) => {
    if (!content) return '';
    
    // First, convert common HTML elements to preserve structure
    let formattedText = content
      // Convert paragraph tags to double line breaks
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p>/gi, '')
      // Convert br tags to single line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Convert list items to bullet points
      .replace(/<li>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      // Remove remaining HTML tags
      .replace(/<[^>]*>/g, '');
    
    // Decode HTML entities after removing tags
    formattedText = decodeHtmlEntities(formattedText);
    
    // Clean up extra whitespace
    formattedText = formattedText
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    // Truncate if needed while preserving word boundaries
    if (formattedText.length > maxLength) {
      formattedText = formattedText.substring(0, maxLength);
      const lastSpace = formattedText.lastIndexOf(' ');
      if (lastSpace > maxLength * 0.8) {
        formattedText = formattedText.substring(0, lastSpace);
      }
      formattedText += '...';
    }
    
    return formattedText;
  };
  
  // Helper: Clean HTML content for date parsing
  const cleanContent = (htmlContent) => {
    if (!htmlContent) return '';
    return htmlContent
      .replace(/<[^>]*>/g, ' ')           // Remove HTML tags
      .replace(/&nbsp;/g, ' ')            // Replace &nbsp; with space
      .replace(/&[a-z]+;/gi, ' ')         // Remove other entities
      .replace(/\s+/g, ' ')               // Normalize whitespace
      .trim();
  };

  // Helper: Parse month name to number (0-11)
  const parseMonthName = (monthStr) => {
    const months = {
      'jan': 0, 'january': 0,
      'feb': 1, 'february': 1,
      'mar': 2, 'march': 2,
      'apr': 3, 'april': 3,
      'may': 4,
      'jun': 5, 'june': 5,
      'jul': 6, 'july': 6,
      'aug': 7, 'august': 7,
      'sep': 8, 'september': 8,
      'oct': 9, 'october': 9,
      'nov': 10, 'november': 10,
      'dec': 11, 'december': 11
    };

    return months[monthStr.toLowerCase()] ?? -1;
  };

  // Helper: Extract dates from text with comprehensive pattern matching
  const extractDatesFromText = (text, currentYear = new Date().getFullYear()) => {
    const allDates = [];

    // Pattern 1: Cross-month ranges "Nov 7 to Dec 6", "Nov 7-Dec 6"
    const crossMonthRangeRegex = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?\s*(?:to|-|through|till)\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?/gi;
    let match;
    while ((match = crossMonthRangeRegex.exec(text)) !== null) {
      const startMonth = parseMonthName(match[1]);
      const startDay = parseInt(match[2]);
      const endMonth = parseMonthName(match[3]);
      const endDay = parseInt(match[4]);

      if (startMonth !== -1 && endMonth !== -1) {
        allDates.push(new Date(currentYear, startMonth, startDay));
        allDates.push(new Date(currentYear, endMonth, endDay));
      }
    }

    // Pattern 2: Same-month ranges "Nov 7-8", "Nov 21-24"
    const sameMonthRangeRegex = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?[-–](\d{1,2})(?:st|nd|rd|th)?(?!\s*[-–to])/gi;
    while ((match = sameMonthRangeRegex.exec(text)) !== null) {
      const month = parseMonthName(match[1]);
      const startDay = parseInt(match[2]);
      const endDay = parseInt(match[3]);

      if (month !== -1) {
        // Add all days in the range
        for (let day = startDay; day <= endDay; day++) {
          allDates.push(new Date(currentYear, month, day));
        }
      }
    }

    // Pattern 3: Reverse format "7-8 November", "21-24 Nov"
    const reverseSameMonthRegex = /\b(\d{1,2})(?:st|nd|rd|th)?[-–](\d{1,2})(?:st|nd|rd|th)?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*/gi;
    while ((match = reverseSameMonthRegex.exec(text)) !== null) {
      const startDay = parseInt(match[1]);
      const endDay = parseInt(match[2]);
      const month = parseMonthName(match[3]);

      if (month !== -1) {
        // Add all days in the range
        for (let day = startDay; day <= endDay; day++) {
          allDates.push(new Date(currentYear, month, day));
        }
      }
    }

    // Pattern 4: Numeric date ranges "11/7/2025 to 12/6/2025"
    const numericRangeRegex = /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\s*(?:to|-|through|till)\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/gi;
    while ((match = numericRangeRegex.exec(text)) !== null) {
      const startMonth = parseInt(match[1]) - 1;
      const startDay = parseInt(match[2]);
      const startYear = parseInt(match[3]);
      const endMonth = parseInt(match[4]) - 1;
      const endDay = parseInt(match[5]);
      const endYear = parseInt(match[6]);

      allDates.push(new Date(startYear, startMonth, startDay));
      allDates.push(new Date(endYear, endMonth, endDay));
    }

    // Pattern 5: Month-year only "November 2025" (default to last day of month)
    const monthYearRegex = /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\b/gi;
    while ((match = monthYearRegex.exec(text)) !== null) {
      const month = parseMonthName(match[1]);
      const year = parseInt(match[2]);

      if (month !== -1) {
        // Use last day of the month
        const lastDay = new Date(year, month + 1, 0).getDate();
        allDates.push(new Date(year, month, lastDay));
      }
    }

    // Pattern 6: Single dates "Dec 6", "November 15" (not part of a range)
    const singleDateRegex = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?(?!\s*[-–to]|\d)/gi;
    while ((match = singleDateRegex.exec(text)) !== null) {
      const month = parseMonthName(match[1]);
      const day = parseInt(match[2]);

      if (month !== -1) {
        allDates.push(new Date(currentYear, month, day));
      }
    }

    // Pattern 7: Numeric single dates "10/17/2025", "12/6/2025"
    const numericDateRegex = /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b(?!\s*(?:to|-|through|till))/gi;
    while ((match = numericDateRegex.exec(text)) !== null) {
      const month = parseInt(match[1]) - 1;
      const day = parseInt(match[2]);
      const year = parseInt(match[3]);

      const date = new Date(year, month, day);
      // Validate the date is reasonable
      if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
        allDates.push(date);
      }
    }

    return allDates;
  };

  // Parse dates from announcement title and content
  const extractDatesFromAnnouncement = (announcement) => {
    // Combine title and cleaned content for parsing
    const cleanedContent = cleanContent(announcement.content || '');
    const searchText = `${announcement.title} ${cleanedContent}`;

    // Debug: Show first 200 chars of search text
    if (announcement.sponsor_enabled) {
      console.log('[Date Parser] Searching in:', searchText.substring(0, 200) + '...');
    }

    // Extract all dates from the combined text
    const dates = extractDatesFromText(searchText);

    // Add scheduled_for as fallback
    if (dates.length === 0 && announcement.scheduled_for) {
      dates.push(new Date(announcement.scheduled_for));
    }

    // Remove duplicates and sort
    const uniqueDates = [...new Set(dates.map(d => d.getTime()))]
      .map(time => new Date(time))
      .sort((a, b) => a - b);

    // Debug logging
    if (announcement.sponsor_enabled) {
      console.log(`[Date Parser] Found ${uniqueDates.length} unique dates for "${announcement.title}":`,
        uniqueDates.map(d => d.toLocaleDateString()));
    }

    return uniqueDates;
  };

  // Check if announcement is for today or upcoming event
  const isEventTodayOrUpcoming = (announcement) => {
    const extractedDates = extractDatesFromAnnouncement(announcement);

    // If no dates found, show button by default (manual control via sponsor_enabled)
    if (extractedDates.length === 0) {
      if (announcement.sponsor_enabled) {
        console.log(`[Date Parser] No dates found for "${announcement.title}", showing sponsor button by default`);
      }
      return true;
    }

    // Get today's date (start of day for comparison)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the LATEST date (end of event period)
    // extractedDates is already sorted in ascending order
    const endDate = new Date(extractedDates[extractedDates.length - 1]);
    endDate.setHours(23, 59, 59, 999); // End of the last day

    // Show button if today <= end date (during the entire event period)
    const showButton = today <= endDate;

    // Debug logging
    if (announcement.sponsor_enabled) {
      const dateRange = extractedDates.length > 1
        ? `${extractedDates[0].toLocaleDateString()} → ${extractedDates[extractedDates.length - 1].toLocaleDateString()}`
        : extractedDates[0].toLocaleDateString();

      console.log(`[Date Parser] Event check for "${announcement.title}":`, {
        dateRange,
        totalDates: extractedDates.length,
        endDate: endDate.toLocaleDateString(),
        today: today.toLocaleDateString(),
        showButton: showButton ? '✅ YES' : '❌ NO (event ended)'
      });
    }

    return showButton;
  };

  // Simple toast utility for user feedback
  const showToast = (message, isError = false) => {
    const toast = document.createElement('div');
    toast.className = 'share-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${isError ? '#f44336' : '#333'};
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 9999;
      animation: slideUp 0.3s ease-out;
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 2000);
  };

  // Share handler with Web Share API Level 2 + image proxy
  const handleShare = async (announcement) => {
    // Use direct function URL with shortened ID (first 8 characters)
    // This uses the same working pattern but with shorter ID parameter
    const shortId = announcement.id.substring(0, 8);
    const shareUrl = `${window.location.origin}/.netlify/functions/share-announcement?id=${shortId}`;
    const shareTitle = announcement.title;
    // Format the share text with friendly message
    const formattedBody = formatShareText(announcement.content);

    // Build share text with simplified single-message donation approach:
    // [Title]
    // Support Dar-e-Zainab (as) [this Friday]: [Link]
    // [Content]
    // 📍 Full Details: [Link]
    // Only include payment link if BOTH payment_link_url exists AND default_donation_amount is set
    // This ensures the toggle state is respected (when toggled OFF, default_donation_amount becomes null)
    const hasPaymentLink = announcement.payment_link_url && announcement.default_donation_amount && isEventTodayOrUpcoming(announcement);

    let shareText = `*${shareTitle}*\n\n`;

    // Include payment link for ALL announcements with payment links
    if (hasPaymentLink) {
      const donationMessage = donationMessagingService.generateDonationMessage(
        announcement,
        announcement.payment_link_url
      );
      shareText += `${donationMessage}\n\n`;
    }

    shareText += `${formattedBody}\n\n📍 Full Details:`;

    const slugify = (s) =>
      (s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

    const getShareImageFile = async () => {
      if (!announcement?.image_url) return null;
      try {
        const imgUrl = new URL(announcement.image_url, window.location.origin);
        const sameOrigin = imgUrl.origin === window.location.origin;
        const proxyUrl = sameOrigin
          ? imgUrl.toString()
          : `${window.location.origin}/.netlify/functions/image-proxy?url=${encodeURIComponent(
              imgUrl.toString()
            )}&filename=${encodeURIComponent(slugify(announcement.title) || 'announcement')}`;

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        const res = await fetch(proxyUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!res.ok) return null;

        const blob = await res.blob();
        const type = blob.type || 'image/jpeg';
        const ext = type.includes('png')
          ? 'png'
          : type.includes('webp')
          ? 'webp'
          : type.includes('gif')
          ? 'gif'
          : 'jpg';
        const filename = `${slugify(announcement.title) || 'announcement'}.${ext}`;
        return new File([blob], filename, { type });
      } catch (e) {
        console.warn('Share image fetch failed, falling back to URL-only share:', e);
        return null;
      }
    };

    try {
      if (navigator.share) {
        let didShareWithFile = false;

        if (announcement?.image_url && navigator.canShare) {
          const imageFile = await getShareImageFile();
          if (imageFile && navigator.canShare({ files: [imageFile] })) {
            await navigator.share({
              title: shareTitle,
              text: shareText,
              url: shareUrl,
              files: [imageFile]
            });
            didShareWithFile = true;
          }
        }

        if (!didShareWithFile) {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: shareUrl
          });
        }

        // Native share dialog provides feedback - no toast needed
      } else {
        // If Web Share API not available, copy the formatted text with URL
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        // Clipboard copy is silent - user initiated share, expects it to work
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        try {
          await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
          showToast('Sharing not supported. Content copied!', true);
        } catch {
          showToast('Failed to share. Please try again.', true);
        }
      }
    }

    // Track analytics for this share
    if (hasPaymentLink) {
      try {
        const isJumua = donationMessagingService.isJumuaAnnouncement(announcement);
        await donationMessagingService.trackDonationMessage({
          announcement_id: announcement.id,
          center_id: config?.center_id,
          variant: isJumua ? 'jumua' : 'standard',
          sent_at: new Date(),
          recipients_count: 1 // Manual share, assume 1 recipient
        });
      } catch (error) {
        console.error('[handleShare] Failed to track analytics:', error);
      }
    }
  };

  // Sort announcements: pinned first, then by date (newest first)
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    // First sort by pinned status
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    // Then sort by created_at descending (newest first)
    return new Date(b.created_at) - new Date(a.created_at);
  });
  
  // Use the custom hook for responsive design
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Determine which layout to use based on device type
  const Layout = isMobile ? MobileLayout : DesktopLayout;

  // Styles matching Penta_Uni_Platform design language
  const styles = {
    container: {
      minHeight: 'calc(100vh - 150px)',
      backgroundColor: theme?.colors?.background || '#f8f9fa',
      padding: '32px 24px',
      fontFamily: theme?.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      maxWidth: '1200px',
      margin: '0 auto 32px',
    },
    titleSection: {
      marginBottom: '24px',
    },
    title: {
      margin: 0,
      fontSize: '32px',
      fontWeight: 'bold',
      color: theme?.colors?.primary || '#1a4173',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px',
    },
    accentBar: {
      display: 'inline-block',
      width: '4px',
      height: '32px',
      backgroundColor: theme?.colors?.primary || '#1a4173',
      borderRadius: '2px',
    },
    subtitle: {
      fontSize: '16px',
      color: theme?.colors?.textSecondary || '#6c757d',
      margin: 0,
      paddingLeft: '16px',
    },
    filterSection: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      marginBottom: '24px',
    },
    filterChip: {
      padding: '8px 16px',
      borderRadius: '20px',
      border: `1px solid ${theme?.colors?.primary || '#1a4173'}`,
      backgroundColor: 'transparent',
      color: theme?.colors?.primary || '#1a4173',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
    },
    filterChipActive: {
      backgroundColor: theme?.colors?.primary || '#1a4173',
      color: 'white',
    },
    announcementsContainer: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      padding: '24px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
    },
    pinnedCard: {
      backgroundColor: `${theme?.colors?.primary || '#1a4173'}08`,
      border: `2px solid ${theme?.colors?.primary || '#1a4173'}`,
    },
    cardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    },
    pinBadge: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      backgroundColor: theme?.colors?.primary || '#1a4173',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: theme?.colors?.cardText || '#333',
      marginBottom: '12px',
      paddingRight: '80px', // Space for pin badge
      lineHeight: '1.3',
    },
    cardContent: {
      fontSize: '15px',
      color: theme?.colors?.cardTextSecondary || '#666',
      lineHeight: '1.6',
      marginBottom: '16px',
    },
    expandedContent: {
      whiteSpace: 'normal',
    },
    readMore: {
      color: theme?.colors?.primary || '#1a4173',
      fontWeight: '500',
      fontSize: '14px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '8px',
    },
    // Desktop layout styles
    desktopLayout: {
      display: 'flex',
      gap: '24px',
      alignItems: 'flex-start',
    },
    desktopContent: {
      flex: 1,
      minWidth: 0, // Allows text to wrap properly
    },
    desktopImage: {
      width: '300px',
      flexShrink: 0,
    },
    imageDesktop: {
      width: '100%',
      height: '200px',
      objectFit: 'cover',
      borderRadius: '8px',
      cursor: 'zoom-in',
      transition: 'transform 0.2s ease',
    },
    imageMobile: {
      width: '100%',
      height: 'auto',
      maxHeight: '250px',
      objectFit: 'cover',
      borderRadius: '8px',
      marginBottom: '16px',
      cursor: 'zoom-in',
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '16px',
      borderTop: '1px solid #eee',
      fontSize: '14px',
      color: theme?.colors?.textMuted || '#999',
    },
    authorChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      backgroundColor: theme?.colors?.secondary || '#f39c12',
      color: 'white',
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
    },
    whatsappBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 8px',
      backgroundColor: '#25d366',
      color: 'white',
      borderRadius: '12px',
      fontSize: '12px',
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '16px',
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: `4px solid ${theme?.colors?.lightGray || '#e0e0e0'}`,
      borderTopColor: theme?.colors?.primary || '#1a4173',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    error: {
      maxWidth: '600px',
      margin: '0 auto',
      padding: '24px',
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '8px',
      color: '#856404',
      textAlign: 'center',
    },
    empty: {
      maxWidth: '600px',
      margin: '0 auto',
      padding: '48px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      textAlign: 'center',
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px',
    },
    emptyText: {
      fontSize: '18px',
      color: theme?.colors?.cardTextSecondary || '#666',
      marginBottom: '8px',
    },
    emptySubtext: {
      fontSize: '14px',
      color: theme?.colors?.textMuted || '#999',
    },
    imageModal: {
      display: 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 1000,
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'zoom-out',
    },
    imageModalOpen: {
      display: 'flex',
    },
    modalImage: {
      maxWidth: '90%',
      maxHeight: '90%',
      objectFit: 'contain',
      borderRadius: '8px',
    },
    closeButton: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      backgroundColor: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '24px',
      fontWeight: 'bold',
    },
    reactionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      backgroundColor: 'transparent',
      border: `1px solid ${theme?.colors?.lightGray || '#e0e0e0'}`,
      borderRadius: '20px',
      fontSize: '14px',
      color: theme?.colors?.cardTextSecondary || '#666',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
      fontFamily: theme?.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    reactionButtonHover: {
      borderColor: theme?.colors?.primary || '#1a4173',
      backgroundColor: `${theme?.colors?.primary || '#1a4173'}05`,
      transform: 'translateY(-1px)',
    },
    titleSeparator: {
      width: '100%',
      height: '1px',
      backgroundColor: theme?.colors?.lightGray || '#e0e0e0',
      marginTop: '12px',
      marginBottom: '16px',
    },
    reactionButtonActive: {
      backgroundColor: `${theme?.colors?.primary || '#1a4173'}10`,
      borderColor: theme?.colors?.primary || '#1a4173',
      color: theme?.colors?.primary || '#1a4173',
      fontWeight: '600',
    },
    shareButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 12px',
      backgroundColor: 'transparent',
      border: `1px solid ${theme?.colors?.lightGray || '#b0b0b0'}`,
      borderRadius: '20px',
      fontSize: '14px',
      color: theme?.colors?.cardTextSecondary || '#666',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
      fontFamily: theme?.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
  };

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loading}>
            <div style={styles.loadingSpinner}></div>
            <div style={{ color: theme?.colors?.primary }}>Loading announcements...</div>
          </div>
        </div>
      </Layout>
    );
  }

  // Render error state
  if (error) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.error}>
            <div>⚠️ {error}</div>
            <button 
              onClick={fetchAnnouncements}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: theme?.colors?.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Render empty state
  if (!sortedAnnouncements || sortedAnnouncements.length === 0) {
    return (
      <Layout>
        <div style={styles.container}>
        <div style={styles.header}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '12px' : '16px' }}>
            <h1 style={{ 
              fontSize: isMobile ? '32px' : '40px', 
              fontWeight: '600',
              margin: isMobile ? '0 0 4px 0' : '0 0 8px 0',
              color: theme?.colors?.primary || '#1a4173'
            }}>
              Announcements
            </h1>
            <div style={{ 
              fontSize: '16px',
              color: theme?.colors?.textSecondary || '#6c757d',
              fontWeight: '500'
            }}>
              Stay updated with the latest news and events from {config?.name}
            </div>
          </div>
        </div>

        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📢</div>
          <div style={styles.emptyText}>
            {expiredAnnouncements.length > 0 ? 'No current announcements' : 'No announcements yet'}
          </div>
          <div style={styles.emptySubtext}>
            {expiredAnnouncements.length > 0
              ? 'Browse past announcements in the archive below'
              : 'Check back later for updates from your community'}
          </div>
        </div>

        {/* Archive section — accessible even when no active announcements */}
        {expiredAnnouncements.length > 0 && (
          <div style={{ marginTop: '24px', borderTop: `1px solid ${theme?.colors?.mediumGray || '#e0e0e0'}`, paddingTop: '20px' }}>
            <button
              onClick={() => setShowArchive(!showArchive)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '16px', fontWeight: 600,
                color: theme?.colors?.cardTextSecondary || '#64748b',
                padding: '8px 0', width: '100%',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}>
                <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>
              </svg>
              Archive ({expiredAnnouncements.length})
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
                style={{ marginLeft: 'auto', transition: 'transform 0.2s', transform: showArchive ? 'rotate(180deg)' : 'rotate(0)' }}
              >
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
              </svg>
            </button>
            {showArchive && (
              <div style={{ marginTop: '12px', opacity: 0.7 }}>
                {expiredAnnouncements.map(ann => (
                  <div key={ann.id} style={{
                    padding: '12px 16px', marginBottom: '8px',
                    borderRadius: '8px', border: `1px solid ${theme?.colors?.mediumGray || '#e2e8f0'}`,
                    background: theme?.colors?.cardBg || '#f8fafc',
                  }}>
                    <div style={{
                      fontSize: '14px', fontWeight: 600,
                      color: theme?.colors?.cardText || '#333',
                      marginBottom: '4px',
                    }}>
                      {ann.title}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: theme?.colors?.cardTextSecondary || '#64748b',
                    }}>
                      Expired {new Date(ann.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {ann.category && ` • ${ann.category}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </Layout>
    );
  }

  // Render announcements
  return (
    <Layout>
      <div style={styles.container} id="announcements-page">
      <div style={styles.header}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '12px' : '16px' }}>
          <h1 style={{ 
            fontSize: isMobile ? '32px' : '40px', 
            fontWeight: '600',
            margin: isMobile ? '0 0 4px 0' : '0 0 8px 0',
            color: theme?.colors?.primary || '#1a4173'
          }}>
            Announcements
          </h1>
          <div style={{ 
            fontSize: '16px',
            color: theme?.colors?.textSecondary || '#6c757d',
            fontWeight: '500'
          }}>
            Stay updated with the latest news and events from {config?.name}
          </div>
        </div>
      </div>

      <div className="announcements-container" style={styles.announcementsContainer}>
        {sortedAnnouncements.map(announcement => {
          const isExpanded = expandedCards.has(announcement.id);
          const cardStyle = announcement.is_pinned 
            ? { ...styles.card, ...styles.pinnedCard }
            : styles.card;

          return (
            <div
              className="announcement-card"
              key={announcement.id}
              id={`announcement-${announcement.id}`}
              style={cardStyle}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, styles.cardHover);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = announcement.is_pinned 
                  ? '0 2px 12px rgba(0,0,0,0.05)' 
                  : '0 2px 12px rgba(0,0,0,0.05)';
              }}
            >
              {announcement.is_pinned && (
                <div style={styles.pinBadge}>
                  📌 Pinned
                </div>
              )}

              {/* Desktop Layout - Side by side */}
              {!isMobile && announcement.image_url ? (
                <div style={styles.desktopLayout}>
                  <div style={styles.desktopContent}>
                    <h3 style={styles.cardTitle}>{announcement.title}</h3>
                    
                    {/* Separator line */}
                    <div style={styles.titleSeparator}></div>
                    
                    {/* Sponsor Widget - Moved here for better visibility */}
                    {(() => {
                      // Only show sponsor widget if: announcement has it enabled AND center has payment configured
                      const centerHasPayment = config?.support_enabled === true || !!config?.payment_link_url;
                      const sponsorEnabled = announcement.sponsor_enabled === true && centerHasPayment;
                      const isTodayOrUpcoming = isEventTodayOrUpcoming(announcement);
                      const shouldShowSponsor = sponsorEnabled && isTodayOrUpcoming;
                      
                      const useNewFlow = true;
                      
                      return shouldShowSponsor && (
                        <div style={{ marginBottom: '20px' }}>
                          {useNewFlow ? (
                            <SponsorshipFlow
                              announcement={announcement}
                              centerConfig={config}
                            />
                          ) : (
                            <SponsorWidget
                              announcement={announcement}
                              centerConfig={config}
                            />
                          )}
                        </div>
                      );
                    })()}
                    
                    <div 
                      className={isExpanded ? 'announcement-content' : 'announcement-content collapsed'}
                      style={isExpanded ? { ...styles.cardContent, ...styles.expandedContent } : styles.cardContent}
                      onClick={() => toggleExpanded(announcement.id)}
                    >
                      <HtmlContent 
                        content={announcement.content}
                        className="announcement-html-content"
                      />
                      {announcement.content && announcement.content.replace(/<[^>]*>/g, '').length > 150 && (
                        <div style={styles.readMore}>
                          {isExpanded ? '▲ Show less' : '▼ Read more'}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={styles.desktopImage}>
                    {isPdfUrl(announcement.image_url) ? (
                      // PDF Display for desktop - Compact with preview
                      <div style={{
                        ...styles.imageDesktop,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9fafb',
                        padding: '15px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}>
                        <svg 
                          width="48" 
                          height="48" 
                          viewBox="0 0 24 24" 
                          fill="#dc2626"
                          style={{ marginBottom: '10px' }}
                        >
                          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                          <path d="M8 15h8v2H8zm0-3h8v2H8z"/>
                        </svg>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>PDF Document</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[AnnouncementsPage] PDF button clicked, URL:', announcement.image_url);
                            if (announcement.image_url) {
                              setPdfUrl(announcement.image_url);
                              setPdfAnnouncement(announcement);
                              console.log('[AnnouncementsPage] PDF viewer should now open');
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#b91c1c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                          </svg>
                          View PDF
                        </button>
                      </div>
                    ) : (
                      // Image Display for desktop
                      <img 
                        src={announcement.image_url} 
                        alt={announcement.title}
                        style={{
                          ...styles.imageDesktop,
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease, opacity 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Open image in fullscreen modal
                          setSelectedImage(announcement.image_url);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.opacity = '0.95';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.opacity = '1';
                        }}
                        title="Click to view full size image"
                      />
                    )}
                  </div>
                </div>
              ) : (
                /* Mobile Layout or No Image - Stacked */
                <>
                  <h3 style={styles.cardTitle}>{announcement.title}</h3>
                  
                  {/* Separator line */}
                  <div style={styles.titleSeparator}></div>
                  
                  {/* Mobile: Image or PDF comes right after title if exists */}
                  {isMobile && announcement.image_url && (
                    isPdfUrl(announcement.image_url) ? (
                      // PDF Display for mobile - Compact
                      <div style={{
                        ...styles.imageMobile,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9fafb',
                        padding: '15px',
                        height: '150px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}>
                        <svg 
                          width="48" 
                          height="48" 
                          viewBox="0 0 24 24" 
                          fill="#dc2626"
                          style={{ marginBottom: '10px' }}
                        >
                          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                          <path d="M8 15h8v2H8zm0-3h8v2H8z"/>
                        </svg>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>PDF Document</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[AnnouncementsPage Mobile] PDF button clicked, URL:', announcement.image_url);
                            if (announcement.image_url) {
                              setPdfUrl(announcement.image_url);
                              setPdfAnnouncement(announcement);
                              console.log('[AnnouncementsPage Mobile] PDF viewer should now open');
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                          </svg>
                          View PDF
                        </button>
                      </div>
                    ) : (
                      // Image Display for mobile
                      <img 
                        src={announcement.image_url} 
                        alt={announcement.title}
                        style={{
                          ...styles.imageMobile,
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease, opacity 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Open image in fullscreen modal
                          setSelectedImage(announcement.image_url);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.opacity = '0.95';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.opacity = '1';
                        }}
                        title="Click to view full size image"
                      />
                    )
                  )}
                  
                  {/* Sponsor Widget - Moved here after image for better visibility */}
                  {(() => {
                    // Default to true unless explicitly set to false
                    const sponsorEnabled = announcement.sponsor_enabled !== false;
                    const isTodayOrUpcoming = isEventTodayOrUpcoming(announcement);
                    const shouldShowSponsor = sponsorEnabled && isTodayOrUpcoming;
                    
                    const useNewFlow = true;
                    
                    return shouldShowSponsor && (
                      <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                        {useNewFlow ? (
                          <SponsorshipFlow
                            announcement={announcement}
                            centerConfig={config}
                          />
                        ) : (
                          <SponsorWidget
                            announcement={announcement}
                            centerConfig={config}
                          />
                        )}
                      </div>
                    );
                  })()}
                  
                  <div 
                    className={isExpanded ? 'announcement-content' : 'announcement-content collapsed'}
                    style={isExpanded ? { ...styles.cardContent, ...styles.expandedContent } : styles.cardContent}
                    onClick={() => toggleExpanded(announcement.id)}
                  >
                    <HtmlContent 
                      content={announcement.content}
                      className="announcement-html-content"
                    />
                    {announcement.content && announcement.content.replace(/<[^>]*>/g, '').length > 150 && (
                      <div style={styles.readMore}>
                        {isExpanded ? '▲ Show less' : '▼ Read more'}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div style={styles.cardFooter}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Reaction Button */}
                  <button
                    onClick={(e) => handleReaction(e, announcement.id)}
                    onMouseEnter={(e) => {
                      if (!userReactions[announcement.id]?.includes('like')) {
                        Object.assign(e.currentTarget.style, styles.reactionButtonHover);
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!userReactions[announcement.id]?.includes('like')) {
                        const defaultBorder = announcement.is_pinned 
                          ? (theme?.colors?.primary || '#1a4173')
                          : (theme?.colors?.lightGray || '#b0b0b0');
                        e.currentTarget.style.borderColor = defaultBorder;
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'none';
                      }
                    }}
                    disabled={processingReaction}
                    style={{
                      ...styles.reactionButton,
                      // Use theme color border for pinned announcements for better visibility
                      ...(announcement.is_pinned && !userReactions[announcement.id]?.includes('like') 
                        ? { borderColor: theme?.colors?.primary || '#1a4173' } 
                        : {}),
                      ...(userReactions[announcement.id]?.includes('like') ? styles.reactionButtonActive : {}),
                      ...(processingReaction ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                    }}
                  >
                    👍 {reactionCounts[announcement.id]?.like || 0}
                  </button>
                  
                  {/* Share Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(announcement);
                    }}
                    style={{
                      ...styles.shareButton,
                      // Use theme color border for pinned announcements for better visibility
                      ...(announcement.is_pinned ? { borderColor: theme?.colors?.primary || '#1a4173' } : {})
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = theme?.colors?.primary || '#1a4173';
                      e.currentTarget.style.backgroundColor = `${theme?.colors?.primary || '#1a4173'}05`;
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      const defaultBorder = announcement.is_pinned 
                        ? (theme?.colors?.primary || '#1a4173')
                        : (theme?.colors?.lightGray || '#b0b0b0');
                      e.currentTarget.style.borderColor = defaultBorder;
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ marginRight: '4px' }}
                    >
                      <circle cx="6" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="18" cy="6" r="2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="18" cy="18" r="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M8 11L16 7M8 13L16 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Share
                  </button>
                  
                  {announcement.whatsapp_message && (
                    <span style={styles.whatsappBadge}>
                      📱 WhatsApp
                    </span>
                  )}
                </div>
                <span>{formatDate(announcement.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            cursor: 'zoom-out',
            padding: '20px'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Full size view"
            style={{
              maxWidth: '90%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button 
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '24px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onClick={() => setSelectedImage(null)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfUrl && pdfAnnouncement && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          title={pdfAnnouncement.title || 'PDF Document'}
          author={pdfAnnouncement.author || ''}
          onClose={() => {
            setPdfUrl(null);
            setPdfAnnouncement(null);
            console.log('[AnnouncementsPage] PDF viewer closed');
          }}
        />
      )}

      {/* Floating Share Button for the whole page */}
      <FloatingShareButton 
        pageTitle="Announcements"
        pageDescription={`Latest announcements and events from ${config?.name || 'our community'}`}
      />

      {/* Payment Success Popup */}
      {showPaymentSuccess && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          zIndex: 10000,
          textAlign: 'center',
          maxWidth: '400px',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>
            ✅
          </div>
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: '#333'
          }}>
            Thank You!
          </h2>
          <p style={{
            margin: '0 0 20px 0',
            fontSize: '16px',
            color: '#666',
            lineHeight: '1.5'
          }}>
            Your donation has been received and a receipt will be sent. May Allah reward you for your support!
          </p>
          <button
            onClick={() => setShowPaymentSuccess(false)}
            style={{
              background: theme?.colors?.primary || '#1a4173',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            Close
          </button>
        </div>
      )}

      {/* Overlay for Payment Success Popup */}
      {showPaymentSuccess && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={() => setShowPaymentSuccess(false)}
        />
      )}

      {/* Archive Section — Expired Announcements */}
      {expiredAnnouncements.length > 0 && (
        <div style={{ marginTop: '32px', borderTop: `1px solid ${theme?.colors?.mediumGray || '#e0e0e0'}`, paddingTop: '24px' }}>
          <button
            onClick={() => setShowArchive(!showArchive)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '16px', fontWeight: 600,
              color: theme?.colors?.cardTextSecondary || '#64748b',
              padding: '8px 0', width: '100%',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}>
              <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>
            </svg>
            Archive ({expiredAnnouncements.length})
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
              style={{ marginLeft: 'auto', transition: 'transform 0.2s', transform: showArchive ? 'rotate(180deg)' : 'rotate(0)' }}
            >
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
            </svg>
          </button>

          {showArchive && (
            <div style={{ marginTop: '12px', opacity: 0.7 }}>
              {expiredAnnouncements.map(ann => (
                <div key={ann.id} style={{
                  padding: '12px 16px', marginBottom: '8px',
                  borderRadius: '8px', border: `1px solid ${theme?.colors?.mediumGray || '#e2e8f0'}`,
                  background: theme?.colors?.cardBg || '#f8fafc',
                }}>
                  <div style={{
                    fontSize: '14px', fontWeight: 600,
                    color: theme?.colors?.cardText || '#333',
                    marginBottom: '4px',
                  }}>
                    {ann.title}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: theme?.colors?.cardTextSecondary || '#64748b',
                  }}>
                    Expired {new Date(ann.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {ann.category && ` • ${ann.category}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      </div>
    </Layout>
  );
};

export default AnnouncementsPage;